import { useConfirmation } from '@components/modal';
import { useDeleteSecretMutation, type Secret } from '@generated/graphql';
import { useNotifications } from '@notifications/Notifications';
import posthog from 'posthog-js';

type DeleteSecretModalProps = {
  secret: Secret;
  onDelete?: VoidFunction;
};

export const useDeleteSecretModal = ({ secret, onDelete }: DeleteSecretModalProps) => {
  const { createAsyncNotification } = useNotifications();
  const { showConfirmation } = useConfirmation();
  const [deleteSecretMutation] = useDeleteSecretMutation();

  const deleteSecret = createAsyncNotification(
    'Secret deleted successfully.',
    async () => {
      posthog.capture('agent-studio:user_delete-secret_submitted', {
        secretId: secret.secretId,
      });
      await deleteSecretMutation({
        variables: { secretId: secret.secretId },
        update(cache) {
          cache.evict({ id: cache.identify(secret) });
          cache.gc();
        },
        onCompleted: () => {
          posthog.capture('agent-studio:user_delete-secret_completed', {
            secretId: secret.secretId,
          });
          onDelete?.();
        },
        onError: (error) => {
          posthog.capture('agent-studio:user_delete-secret_failed', {
            secretId: secret.secretId,
            error: (error as Error)?.message || 'An error occurred during deletion',
          });
          throw error;
        },
      });
    },
    {
      loadingMessage: 'Deleting secret...',
    },
  );

  const showModal = () =>
    showConfirmation({
      onAffirm: deleteSecret,
      message: `Are you sure you want to delete ${secret?.name || 'this secret'}?`,
    });

  return { showModal };
};
